import { useNavigate } from "react-router-dom";
import { Calendar, Users, BarChart3 } from "lucide-react";
import { useUserContext } from "@/contexts/UserContext";

const Index = () => {
  const navigate = useNavigate();
  const { user, profile } = useUserContext();

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      {/* ── Nav ── */}
      <header className="flex items-center justify-between h-[72px] px-6 md:px-12 bg-surface-1/80 backdrop-blur-md border-b border-border sticky top-0 z-50">
        <div className="flex items-center gap-1">
          <span className="text-pulse-green brand-text text-2xl">+</span>
          <span className="text-foreground brand-text text-[22px]">SportHub CL</span>
        </div>
        <nav className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-muted-foreground text-sm hover:text-foreground transition-colors">Características</a>
          <a href="#pricing" className="text-muted-foreground text-sm hover:text-foreground transition-colors">Precios</a>
          <a href="#testimonios" className="text-muted-foreground text-sm hover:text-foreground transition-colors">Testimonios</a>
        </nav>
        {user ? (
          <button
            onClick={() => {
              const role = profile?.role;
              if (role === 'admin') navigate('/admin/dashboard');
              else if (role === 'venue_owner') navigate('/venue/dashboard');
              else if (role === 'coach') navigate('/coach/dashboard');
              else if (role === 'player') navigate('/app/home');
              else navigate('/complete-profile');
            }}
            className="bg-primary text-primary-foreground text-sm font-bold px-6 py-2.5 rounded-lg hover:brightness-110 transition-all active:scale-95"
          >
            Ir al Panel
          </button>
        ) : (
          <button
            onClick={() => navigate("/login")}
            className="bg-surface-3 text-foreground text-sm font-semibold px-6 py-2.5 rounded-lg border border-border hover:bg-surface-4 transition-colors"
          >
            Iniciar Sesión
          </button>
        )}
      </header>

      {/* ── Hero ── */}
      <section className="bg-gradient-hero relative overflow-hidden">
        <div className="flex flex-col lg:flex-row min-h-[720px] max-w-[1400px] mx-auto">
          {/* Left Column */}
          <div className="flex flex-col justify-center gap-6 p-10 md:p-16 lg:w-[600px] shrink-0 z-10">
            <span className="text-pulse-green text-xs font-bold tracking-[3px] uppercase">
              + Gestión Deportiva Inteligente
            </span>
            <h1 className="text-foreground font-display text-5xl md:text-[76px] leading-[0.95] uppercase">
              Más Reservas,{"\n"}Menos Estrés
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-[480px]">
              Plataforma integral para centros deportivos, entrenadores y jugadores en Chile
            </p>
            <button
              onClick={() => navigate("/register")}
              className="bg-primary text-primary-foreground text-base font-bold px-8 py-4 rounded-lg w-fit hover:brightness-110 transition-all active:scale-95 glow-primary"
            >
              Comienza Gratis
            </button>
          </div>

          {/* Right Column — Photo & Sport Tags */}
          <div className="hidden lg:flex flex-col flex-1 p-16 gap-10">
            <div className="flex-1 rounded-xl overflow-hidden shadow-card-lg ring-1 ring-white/5">
              <img
                src="https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=800&q=80"
                alt="Deportista entrenando"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex justify-end gap-6">
              <span className="text-muted-foreground text-xs font-bold tracking-[3px] uppercase">Pádel</span>
              <span className="text-muted-foreground text-xs font-bold tracking-[3px] uppercase">Tenis</span>
              <span className="text-muted-foreground text-xs font-bold tracking-[3px] uppercase">Futsal</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-24 px-6 md:px-12">
        <div className="flex flex-col items-center gap-14 max-w-[1200px] mx-auto">
          <div className="flex flex-col items-center gap-4 text-center">
            <span className="text-pulse-green text-xs font-bold tracking-[3px] uppercase">
              + Lo que ofrecemos
            </span>
            <h2 className="font-display text-3xl md:text-5xl text-center uppercase">
              Todo lo que tu centro necesita
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 w-full">
            {[
              {
                icon: Calendar,
                title: "Reservas en Tiempo Real",
                desc: "Calendario de disponibilidad y reservas directas",
              },
              {
                icon: Users,
                title: "Gestión de Coaches",
                desc: "Vinculación por ID único, comisiones configurables",
              },
              {
                icon: BarChart3,
                title: "Analíticas Avanzadas",
                desc: "Dashboard con KPIs, ingresos y ocupación",
              },
            ].map((feat, i) => (
              <div
                key={i}
                className="flex flex-col gap-4 p-8 rounded-xl bg-card border border-border card-hover"
              >
                <feat.icon className="h-8 w-8 text-pulse-green" strokeWidth={2} />
                <h3 className="text-foreground text-lg font-semibold">{feat.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Showcase — Multi-Deporte ── */}
      <section className="py-24 px-6 md:px-12 bg-surface-1">
        <div className="flex flex-col items-center gap-12 max-w-[1200px] mx-auto">
          <div className="flex flex-col items-center gap-4 text-center">
            <h2 className="font-display text-3xl md:text-5xl text-center uppercase">
              Multi-Deporte, Multi-Centro
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-[600px] text-center">
              Soporte para 7+ deportes y múltiples sedes desde una sola plataforma
            </p>
          </div>

          <div className="flex items-center gap-0 w-full h-[480px]">
            <div className="flex-1 h-full rounded-xl overflow-hidden ring-1 ring-white/5">
              <img
                src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=80"
                alt="Cancha de pádel"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col items-center justify-center gap-4 w-[60px] shrink-0">
              {[1, 2, 3, 4, 5].map((_, i) => (
                <span key={i} className="text-pulse-green brand-text text-2xl">+</span>
              ))}
            </div>
            <div className="flex-1 h-full rounded-xl overflow-hidden ring-1 ring-white/5">
              <img
                src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&q=80"
                alt="Entrenamiento grupal"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="py-24 px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-center max-w-[1200px] mx-auto">
          {[
            { num: "500+", label: "Centros" },
            { num: "2,000+", label: "Entrenadores" },
            { num: "50,000+", label: "Reservas/Mes" },
          ].map((stat, i) => (
            <div key={i} className="flex flex-col items-center gap-2 flex-1 py-8">
              <span className="font-display text-5xl md:text-7xl text-foreground">{stat.num}</span>
              <span className="text-muted-foreground text-xs font-bold tracking-[3px] uppercase">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="mx-6 md:mx-12 bg-surface-1 rounded-2xl py-20 px-10 md:px-16 flex flex-col items-center gap-6 text-center border border-border">
        <span className="text-pulse-green brand-text text-[32px]">+</span>
        <h2 className="font-display text-4xl md:text-[72px] text-center leading-tight uppercase">
          Empieza Hoy
        </h2>
        <p className="text-muted-foreground text-lg leading-relaxed max-w-[560px] text-center">
          Digitaliza tu centro deportivo y aumenta tus reservas desde el primer día
        </p>
        <button
          onClick={() => navigate("/register")}
          className="bg-primary text-primary-foreground text-base font-bold px-8 py-4 rounded-lg hover:brightness-110 transition-all active:scale-95 glow-primary"
        >
          Crear Cuenta Gratis
        </button>
      </section>

      {/* ── Footer ── */}
      <footer className="flex items-center justify-between h-20 px-6 md:px-12 border-t border-border mt-12">
        <div className="flex items-center gap-1">
          <span className="text-pulse-green brand-text text-xl">+</span>
          <span className="text-foreground brand-text text-lg">SportHub CL</span>
        </div>
        <nav className="hidden md:flex items-center gap-6">
          <a href="#features" className="text-muted-foreground text-sm hover:text-foreground transition-colors">Características</a>
          <a href="#pricing" className="text-muted-foreground text-sm hover:text-foreground transition-colors">Precios</a>
          <a href="#testimonios" className="text-muted-foreground text-sm hover:text-foreground transition-colors">Testimonios</a>
        </nav>
        <span className="text-muted-foreground text-[13px]">© 2026 SportHub CL</span>
      </footer>
    </div>
  );
};

export default Index;